import { useState, useEffect } from 'react';
import { useAuth } from '../components/Auth/AuthProvider';
import { 
  createSnippet, 
  updateSnippet, 
  deleteSnippet, 
  subscribeToSnippets 
} from '../services/database';
import {
  getLocalSnippets,
  addLocalSnippet,
  updateLocalSnippet,
  deleteLocalSnippet
} from '../services/localStorage';
import { getUserIdFromUserWithLegacySupport } from '../utils/customUserId';

export const useSnippets = () => {
  const { user } = useAuth();
  const [snippets, setSnippets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOffline, setIsOffline] = useState(false);

  useEffect(() => {
    if (!user) {
      setSnippets([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    // Use custom user ID for local storage compatibility
    const customUserId = getUserIdFromUserWithLegacySupport(user);
    
    // Load local snippets immediately
    const localSnippets = getLocalSnippets(customUserId);
    setSnippets(localSnippets.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)));
    
    console.log(`Loading snippets for user ${user.email} (Custom ID: ${customUserId})`);
    
    // Set a maximum loading time for Firebase
    const loadingTimeout = setTimeout(() => {
      setLoading(false);
      setIsOffline(true);
      setError('Working offline. Your snippets are saved locally.');
    }, 5000); // 5 second timeout
    
    const unsubscribe = subscribeToSnippets(user, (snippetsData) => {
      clearTimeout(loadingTimeout);
      setSnippets(snippetsData.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)));
      setLoading(false);
      setError(null);
      setIsOffline(false);
    });

    return () => {
      clearTimeout(loadingTimeout);
      unsubscribe();
    };
  }, [user]);

  const addSnippet = async (snippetData) => {
    if (!user) throw new Error('User not authenticated');
    
    const customUserId = getUserIdFromUserWithLegacySupport(user);
    
    try {
      let newSnippet;
      if (isOffline) {
        // Use local storage with custom user ID
        newSnippet = addLocalSnippet(customUserId, snippetData);
        setSnippets(prev => [newSnippet, ...prev]);
      } else {
        // Try Firebase first with full user object
        newSnippet = await createSnippet(user, snippetData);
      }
      return newSnippet;
    } catch (error) {
      // Fallback to local storage
      try {
        const newSnippet = addLocalSnippet(customUserId, snippetData);
        setSnippets(prev => [newSnippet, ...prev]);
        setError('Saved locally. Will sync when connection is restored.');
        return newSnippet;
      } catch (localError) {
        setError(localError.message);
        throw localError;
      }
    }
  };

  const editSnippet = async (snippetId, updates) => {
    if (!user) throw new Error('User not authenticated');
    
    const customUserId = getUserIdFromUserWithLegacySupport(user);
    
    try {
      if (isOffline) {
        // Use local storage with custom user ID
        updateLocalSnippet(customUserId, snippetId, updates);
        setSnippets(prev => prev.map(s => s.id === snippetId ? { ...s, ...updates } : s));
      } else {
        // Try Firebase first with full user object
        await updateSnippet(user, snippetId, updates);
      }
    } catch (error) {
      // Fallback to local storage
      try {
        updateLocalSnippet(customUserId, snippetId, updates);
        setSnippets(prev => prev.map(s => s.id === snippetId ? { ...s, ...updates } : s));
        setError('Updated locally. Will sync when connection is restored.');
      } catch (localError) {
        setError(localError.message);
        throw localError;
      }
    }
  };

  const removeSnippet = async (snippetId) => {
    if (!user) throw new Error('User not authenticated');
    
    const customUserId = getUserIdFromUserWithLegacySupport(user);
    
    try {
      if (isOffline) {
        // Use local storage with custom user ID
        deleteLocalSnippet(customUserId, snippetId);
        setSnippets(prev => prev.filter(s => s.id !== snippetId));
      } else {
        // Try Firebase first with full user object
        await deleteSnippet(user, snippetId);
      }
    } catch (error) {
      // Fallback to local storage
      try {
        deleteLocalSnippet(customUserId, snippetId);
        setSnippets(prev => prev.filter(s => s.id !== snippetId));
        setError('Deleted locally. Will sync when connection is restored.');
      } catch (localError) {
        setError(localError.message);
        throw localError;
      }
    }
  };

  const getSnippetByTag = (tag) => {
    return snippets.find(snippet => snippet.tag === tag);
  };

  const searchSnippets = (query) => {
    if (!query.trim()) return snippets;
    
    const lowerQuery = query.toLowerCase();
    return snippets.filter(snippet => 
      snippet.tag.toLowerCase().includes(lowerQuery) ||
      snippet.title.toLowerCase().includes(lowerQuery) ||
      snippet.content.toLowerCase().includes(lowerQuery)
    );
  };

  const getSnippetsByLanguage = (language) => {
    return snippets.filter(snippet => snippet.language === language);
  };

  const clearError = () => setError(null);

  return {
    snippets,
    loading,
    error,
    isOffline,
    addSnippet,
    editSnippet,
    removeSnippet,
    getSnippetByTag,
    searchSnippets,
    getSnippetsByLanguage,
    clearError
  };
};
